import { useState, useEffect, useMemo } from "react";
import {
  auth, onAuth, signIn, signUp, signOutUser, resetPassword, changeMyPassword,
  getVendorAccount, getVendorById, saveVendorAccount, linkVendorAccount,
  subscribeVendorReferrals, updateReferral, subscribeNotifications, markNotificationRead,
} from "./firebase";
import { commStatus, money, fetchRates, toINR, parseNum, CURRENCIES } from "./commission";

/* ═══════════════════════════════════════════════════════════════
   Vendor Portal  ( /vendors )  — Phase 2 + commission loop
═══════════════════════════════════════════════════════════════ */

const INK = "#1c1a17", INK2 = "#4a463e", MUTE = "#8f8a7e";
const LINE = "#e7e3d8", PAPER = "#f3f1ea", GOLD = "#F5C518";
const LOGO_URL = "https://fm.neerajmandhana.com/logo512.png";

// NM Squad's bank account — where vendors send their commission payments.
const NM_BANK = [
  ["Company", "N MANDHANA & PARTNERS LLP"],
  ["Bank", "Kotak Mahindra Bank Limited"],
  ["Account no.", "8150434959"],
  ["IFSC", "KKBK0001365"],
  ["SWIFT", "KKBKINBB"],
  ["Branch", "Hubtown Solaris, Andheri East, Mumbai"],
];

const STATUSES = ["Recommended", "In Progress", "Signed Up", "Did Not Sign Up", "Cancelled"];
const VENDOR_ACTIONS = ["In Progress", "Signed Up", "Did Not Sign Up", "Cancelled"];
const STATUS_FG = {
  "Recommended": "#1d4ed8", "In Progress": "#b45309", "Signed Up": "#15803d",
  "Did Not Sign Up": "#b91c1c", "Cancelled": "#6b7280",
};
const STATUS_BG = {
  "Recommended": "#e8eefc", "In Progress": "#fbeedb", "Signed Up": "#e3f3e8",
  "Did Not Sign Up": "#fbe6e6", "Cancelled": "#ecebe6",
};

const STYLE = `
.vp-in{width:100%;padding:12px 14px;font-size:14px;font-family:inherit;color:#1c1a17;
  background:#fff;border:1px solid #ddd8c9;border-radius:10px;outline:none;box-sizing:border-box;
  transition:border-color .14s,box-shadow .14s;}
.vp-in::placeholder{color:#b6b0a0;}
.vp-in:focus{border-color:#F5C518;box-shadow:0 0 0 3px rgba(245,197,24,.2);}
.vp-btn{font-family:inherit;cursor:pointer;border:none;border-radius:10px;font-weight:800;
  transition:transform .12s,box-shadow .14s,background .12s,border-color .12s,color .12s;}
.vp-gold{background:#F5C518;color:#1c1a17;box-shadow:0 1px 2px rgba(0,0,0,.14);}
.vp-gold:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(245,197,24,.42);}
.vp-gold:disabled{background:#dcd8cc;color:#94907f;box-shadow:none;transform:none;cursor:default;}
.vp-ghost{background:transparent;color:#1c1a17;border:1px solid #ddd8c9;font-weight:700;}
.vp-ghost:hover{border-color:#1c1a17;}
.vp-link{background:none;border:none;cursor:pointer;font-family:inherit;color:#8a7415;font-weight:700;}
.vp-link:hover{text-decoration:underline;}
.vp-chip{font-family:inherit;cursor:pointer;border-radius:999px;font-size:12px;font-weight:700;padding:6px 13px;transition:all .12s;}
.vp-navbtn{font-family:inherit;cursor:pointer;background:none;border:none;font-size:13.5px;font-weight:700;
  padding:6px 2px;color:#9c978a;transition:color .12s;}
.vp-navbtn:hover{color:#fff;}
.vp-navbtn-on{color:#fff;border-bottom:2px solid #F5C518;}
@keyframes vppulse{0%,100%{opacity:1}50%{opacity:.5}}
.vp-blink{animation:vppulse 1.15s ease-in-out infinite;}
.vp-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;
  padding:0 5px;border-radius:9px;background:#dc2626;color:#fff;font-size:11px;font-weight:800;}
`;

const card = {
  background: "#fff", border: `1px solid ${LINE}`, borderRadius: 16,
  boxShadow: "0 1px 2px rgba(28,26,23,.04), 0 12px 34px -14px rgba(28,26,23,.16)",
};
const labelS = { fontSize: 11, fontWeight: 700, color: MUTE, letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6 };
const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
const today = () => new Date().toISOString().slice(0, 10);
function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

/* ════════════════ ROOT ════════════════ */
export default function VendorPortal() {
  const [phase, setPhase] = useState("loading");
  const [account, setAccount] = useState(null);
  const [vendor, setVendor] = useState(null);

  useEffect(() => { document.title = "NM Squad — Partner Portal"; }, []);
  useEffect(() => {
    return onAuth(async (user) => {
      if (!user) { setPhase("login"); setAccount(null); setVendor(null); return; }
      try {
        let acc = await getVendorAccount(user.uid);
        if (!acc) acc = await linkVendorAccount(user.uid, user.email);
        if (!acc) { setPhase("notvendor"); return; }
        const v = await getVendorById(acc.vendorId);
        setAccount(acc); setVendor(v);
        setPhase(acc.onboarded ? "dashboard" : "onboarding");
      } catch (e) { setPhase("notvendor"); }
    });
  }, []);

  if (phase === "loading")
    return <Centered><div style={{ color: MUTE, fontSize: 14 }}>Loading…</div></Centered>;
  if (phase === "login") {
    const setupEmail = new URLSearchParams(window.location.search).get("setup");
    return setupEmail ? <SetupPassword email={setupEmail.trim()} /> : <Login />;
  }
  if (phase === "notvendor")
    return (
      <Centered>
        <div style={{ ...card, padding: "34px 32px", maxWidth: 380, textAlign: "center" }}>
          <Brandmark />
          <div style={{ fontSize: 16, fontWeight: 800, color: INK, marginTop: 16 }}>Not a partner account</div>
          <div style={{ fontSize: 13.5, color: MUTE, margin: "8px 0 18px", lineHeight: 1.5 }}>
            This email is not registered as an NM Squad partner. Use the email NM Squad invited.
          </div>
          <button className="vp-btn vp-ghost" style={{ padding: "10px 20px", fontSize: 14 }} onClick={() => signOutUser()}>Sign out</button>
        </div>
      </Centered>
    );
  if (phase === "onboarding")
    return <Onboarding vendor={vendor} account={account} onDone={(acc) => { setAccount(acc); setPhase("dashboard"); }} />;
  return <Dashboard vendor={vendor} account={account} onAccountChange={setAccount} />;
}

/* ════════════════ shell ════════════════ */
function Centered({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: PAPER, fontFamily: "Helvetica, Arial, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <style>{STYLE}</style>{children}
    </div>
  );
}
function Brandmark({ light }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, justifyContent: "center" }}>
      <img src={LOGO_URL} width={40} height={40} alt="NM Squad" style={{ display: "block" }} />
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: light ? "#fff" : INK }}>NM Squad</div>
        <div style={{ fontSize: 10, fontWeight: 700, color: GOLD, letterSpacing: ".14em", textTransform: "uppercase" }}>Partner Portal</div>
      </div>
    </div>
  );
}

/* ════════════════ SET PASSWORD ════════════════ */
function SetupPassword({ email }) {
  const [pw, setPw] = useState(""), [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [exists, setExists] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr("Choose a password of at least 8 characters.");
    if (pw !== pw2) return setErr("The two passwords do not match.");
    setBusy(true);
    try { await signUp(email, pw); }
    catch (ex) {
      const c = ex.code || "";
      if (c.includes("email-already-in-use")) { setExists(true); setErr(""); }
      else if (c.includes("weak-password")) setErr("That password is too weak — use at least 8 characters.");
      else if (c.includes("invalid-email")) setErr("The invite link is invalid — ask NM Squad to resend it.");
      else setErr("Could not set your password. " + (ex.message || ""));
      setBusy(false);
    }
  };
  if (exists)
    return (
      <Centered>
        <div style={{ ...card, padding: "34px 32px", maxWidth: 388, width: "100%", textAlign: "center" }}>
          <Brandmark />
          <h1 style={{ fontSize: 19, fontWeight: 800, color: INK, margin: "20px 0 6px" }}>You are already set up</h1>
          <p style={{ fontSize: 13.5, color: MUTE, margin: "0 0 20px", lineHeight: 1.55 }}>
            A password has already been set for {email}. Sign in with it.
          </p>
          <a href="/vendors" className="vp-btn vp-gold" style={{ display: "block", padding: "12px", fontSize: 14, textDecoration: "none" }}>Go to sign in</a>
        </div>
      </Centered>
    );
  return (
    <Centered>
      <form onSubmit={submit} style={{ ...card, padding: "34px 32px", maxWidth: 388, width: "100%" }}>
        <Brandmark />
        <h1 style={{ fontSize: 20, fontWeight: 800, color: INK, margin: "22px 0 4px", textAlign: "center" }}>Set your password</h1>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 20px", textAlign: "center", lineHeight: 1.55 }}>
          Welcome to the NM Squad partner portal. Choose a password to finish setting up.
        </p>
        <div style={{ marginBottom: 12 }}><div style={labelS}>Your email</div>
          <input className="vp-in" value={email} disabled style={{ background: PAPER, color: INK2 }} /></div>
        <div style={{ marginBottom: 12 }}><div style={labelS}>Password</div>
          <input className="vp-in" type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" /></div>
        <div style={{ marginBottom: 18 }}><div style={labelS}>Confirm password</div>
          <input className="vp-in" type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" /></div>
        {err && <Err>{err}</Err>}
        <button className="vp-btn vp-gold" type="submit" disabled={busy} style={{ width: "100%", padding: "13px", fontSize: 14.5 }}>
          {busy ? "Setting up…" : "Set password and continue"}
        </button>
      </form>
    </Centered>
  );
}

/* ════════════════ SIGN IN ════════════════ */
function Login() {
  const [email, setEmail] = useState(""), [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [note, setNote] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setNote("");
    if (!isEmail(email)) return setErr("Enter a valid email address.");
    setBusy(true);
    try { await signIn(email, pw); }
    catch (ex) {
      const c = ex.code || "";
      setErr(c.includes("invalid-credential") || c.includes("wrong-password") || c.includes("user-not-found")
        ? "Incorrect email or password."
        : c.includes("too-many-requests") ? "Too many attempts — try again shortly."
        : "Sign-in failed. " + (ex.message || ""));
      setBusy(false);
    }
  };
  const forgot = async () => {
    setErr(""); setNote("");
    if (!isEmail(email)) return setErr("Type your email above first.");
    try { await resetPassword(email); setNote("Password reset link sent to " + email.trim() + "."); }
    catch (ex) { setErr("Could not send the link. " + (ex.message || "")); }
  };
  return (
    <Centered>
      <form onSubmit={submit} style={{ ...card, padding: "34px 32px", maxWidth: 388, width: "100%" }}>
        <Brandmark />
        <h1 style={{ fontSize: 20, fontWeight: 800, color: INK, margin: "22px 0 4px", textAlign: "center" }}>Partner sign in</h1>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 22px", textAlign: "center", lineHeight: 1.55 }}>
          Setting up for the first time? Use the link in NM Squad's invite email.
        </p>
        <div style={{ marginBottom: 12 }}><div style={labelS}>Email</div>
          <input className="vp-in" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" placeholder="you@example.com" /></div>
        <div style={{ marginBottom: 18 }}><div style={labelS}>Password</div>
          <input className="vp-in" type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="current-password" /></div>
        {err && <Err>{err}</Err>}
        {note && <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: "#15803d" }}>{note}</div>}
        <button className="vp-btn vp-gold" type="submit" disabled={busy} style={{ width: "100%", padding: "13px", fontSize: 14.5 }}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button type="button" className="vp-link" onClick={forgot} style={{ fontSize: 12.5, color: MUTE }}>Forgot password?</button>
        </div>
      </form>
    </Centered>
  );
}

/* ════════════════ ONBOARDING ════════════════ */
const BANK_FIELDS = [["bankName", "Bank name"], ["accountName", "Account holder name"], ["accountNumber", "Account number"], ["ifsc", "IFSC code"], ["upi", "UPI ID (optional)"]];
const hS = { fontSize: 19, fontWeight: 800, color: INK, margin: "6px 0 6px" };
const pS = { fontSize: 13.5, color: INK2, lineHeight: 1.6, margin: "0 0 16px" };
const btnS = { flex: 1, padding: "12px", fontSize: 14 };
function Err({ children }) { return <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: "#b91c1c" }}>{children}</div>; }

function Onboarding({ vendor, account, onDone }) {
  const [step, setStep] = useState(1);
  const [mouOk, setMouOk] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [bank, setBank] = useState({ accountName: "", accountNumber: "", ifsc: "", bankName: "", upi: "" });
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const setB = (k) => (e) => setBank({ ...bank, [k]: e.target.value });
  const next1 = () => { setErr(""); if (!mouOk) return setErr("Please confirm you have reviewed the MoU."); setStep(2); };
  const finish = async () => {
    setErr("");
    if (!bank.bankName.trim() || !bank.accountName.trim() || !bank.accountNumber.trim() || !bank.ifsc.trim())
      return setErr("Bank name, account holder, account number and IFSC are all required.");
    setBusy(true);
    try {
      const data = { onboarded: true, mouAcceptedAt: new Date().toISOString(), currency, bankDetails: bank, onboardedAt: new Date().toISOString() };
      await saveVendorAccount(auth.currentUser.uid, data);
      onDone({ ...account, ...data });
    } catch (ex) { setErr("Could not save. " + (ex.message || "")); setBusy(false); }
  };
  return (
    <Centered>
      <div style={{ ...card, maxWidth: 460, width: "100%", padding: 0, overflow: "hidden" }}>
        <div style={{ background: INK, padding: "20px 28px" }}><Brandmark light /></div>
        <div style={{ height: 4, background: GOLD }} />
        <div style={{ padding: "26px 28px" }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
            {[1, 2].map(n => <div key={n} style={{ flex: 1, height: 4, borderRadius: 4, background: n <= step ? GOLD : LINE }} />)}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: MUTE, letterSpacing: ".1em", marginBottom: 4 }}>STEP {step} OF 2</div>
          {step === 1 && (
            <>
              <h2 style={hS}>Your agreement</h2>
              <p style={pS}>Welcome{vendor ? ", " + (vendor.pocName || vendor.organization) : ""}. Please review the Memorandum of Understanding between {vendor ? vendor.organization : "your organisation"} and NM Squad.</p>
              {vendor && vendor.mouUrl
                ? <a href={vendor.mouUrl} target="_blank" rel="noreferrer"
                    style={{ display: "block", textAlign: "center", padding: "13px", marginBottom: 16, borderRadius: 10, border: `1px solid ${LINE}`, background: PAPER, color: INK, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                    Read the MoU document
                  </a>
                : <p style={{ ...pS, color: MUTE }}>Your MoU will be shared with you separately by NM Squad.</p>}
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: INK2, cursor: "pointer", margin: "6px 0 16px" }}>
                <input type="checkbox" checked={mouOk} onChange={e => setMouOk(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16 }} />
                <span>I have reviewed and agree to the terms of the MoU.</span>
              </label>
              {err && <Err>{err}</Err>}
              <button className="vp-btn vp-gold" onClick={next1} style={btnS}>Continue</button>
            </>
          )}
          {step === 2 && (
            <>
              <h2 style={hS}>Currency &amp; bank details</h2>
              <p style={pS}>Choose the currency you work in (you can change it later in My Account). Bank details are held for any refund due under the MoU.</p>
              <div style={{ marginBottom: 14 }}>
                <div style={labelS}>Your currency</div>
                <CurrencySelect value={currency} onChange={e => setCurrency(e.target.value)} />
              </div>
              {BANK_FIELDS.map(([k, l]) => (
                <div key={k} style={{ marginBottom: 11 }}><div style={labelS}>{l}</div>
                  <input className="vp-in" value={bank[k]} onChange={setB(k)} /></div>
              ))}
              {err && <Err>{err}</Err>}
              <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                <button className="vp-btn vp-ghost" onClick={() => setStep(1)} style={{ ...btnS, width: "auto", padding: "12px 18px" }}>Back</button>
                <button className="vp-btn vp-gold" onClick={finish} disabled={busy} style={btnS}>{busy ? "Finishing…" : "Finish setup"}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </Centered>
  );
}

/* ════════════════ DASHBOARD ════════════════ */
function Dashboard({ vendor, account, onAccountChange }) {
  const [view, setView] = useState("students");
  const [referrals, setReferrals] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [rates, setRates] = useState(null);
  useEffect(() => {
    if (!account) return;
    const u1 = subscribeVendorReferrals(account.vendorId, setReferrals, () => setReferrals([]));
    const u2 = subscribeNotifications(account.vendorId, setNotifs, () => setNotifs([]));
    return () => { u1 && u1(); u2 && u2(); };
  }, [account]);
  useEffect(() => { fetchRates().then(setRates); }, []);
  const unread = notifs.filter(n => !n.read).length;
  const NAV = [["students", "Students"], ["inbox", "Inbox"], ["account", "My account"]];

  return (
    <div style={{ minHeight: "100vh", background: PAPER, fontFamily: "Helvetica, Arial, sans-serif" }}>
      <style>{STYLE}</style>
      <div style={{ background: INK, padding: "13px 26px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Brandmark light />
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          {NAV.map(([k, label]) => (
            <button key={k} className={"vp-navbtn" + (view === k ? " vp-navbtn-on" : "")} onClick={() => setView(k)}>
              {label}{k === "inbox" && unread > 0 && <span className="vp-badge vp-blink" style={{ marginLeft: 6 }}>{unread}</span>}
            </button>
          ))}
          <button className="vp-btn vp-ghost" onClick={() => signOutUser()}
            style={{ fontSize: 12.5, padding: "7px 13px", background: "transparent", color: "#cfcabb", borderColor: "#3a372f" }}>Sign out</button>
        </div>
      </div>
      <div style={{ height: 4, background: GOLD }} />
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 26px 60px" }}>
        {view === "students" && <StudentsView vendor={vendor} referrals={referrals} rates={rates} vendorCurrency={account && account.currency} />}
        {view === "inbox" && <InboxView notifs={notifs} />}
        {view === "account" && <AccountView vendor={vendor} account={account} onAccountChange={onAccountChange} />}
      </div>
    </div>
  );
}

/* ── Inbox ── */
function InboxView({ notifs }) {
  const open = (n) => { if (!n.read) markNotificationRead(n.id); };
  return (
    <div>
      <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 800, color: INK }}>Inbox</h1>
      <p style={{ margin: "0 0 20px", fontSize: 14, color: INK2 }}>Messages and commission requests from NM Squad.</p>
      {notifs.length === 0 && (
        <div style={{ ...card, padding: "48px 30px", textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>No messages</div>
          <div style={{ fontSize: 13.5, color: MUTE, marginTop: 6 }}>Messages from NM Squad will appear here.</div>
        </div>
      )}
      {notifs.map(n => (
        <div key={n.id} onClick={() => open(n)} style={{ ...card, padding: "16px 20px", marginBottom: 10,
          cursor: n.read ? "default" : "pointer", borderLeft: n.read ? `1px solid ${LINE}` : `4px solid ${GOLD}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: INK }}>
              {!n.read && <span style={{ color: "#dc2626" }}>● </span>}{n.title}
            </div>
            <div style={{ fontSize: 12, color: MUTE, whiteSpace: "nowrap" }}>{fmtDate(n.createdAt)}</div>
          </div>
          {n.body && <div style={{ fontSize: 13.5, color: INK2, marginTop: 6, lineHeight: 1.55 }}>{n.body}</div>}
        </div>
      ))}
    </div>
  );
}

/* ════════════════ STUDENTS ════════════════ */
const vth = { padding: "11px 12px", fontSize: 10.5, color: MUTE, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 800, textAlign: "left" };
const vtd = { padding: "11px 12px", verticalAlign: "top", textAlign: "left" };

function CommissionTag({ r, rates }) {
  const cs = commStatus(r);
  if (cs === "none") return <span style={{ color: MUTE }}>—</span>;
  if (cs === "received") return <span style={{ fontSize: 12, fontWeight: 800, color: "#15803d" }}>Received</span>;
  if (cs === "paid") return <span style={{ fontSize: 12, fontWeight: 800, color: "#8a5a06" }}>Paid</span>;
  const inr = toINR(r.commAmount, r.commCurrency, rates);
  return <span className="vp-blink" style={{ fontSize: 12, fontWeight: 800, color: "#b91c1c" }}>
    {inr != null ? "Rs " + money(inr) + " due" : "Pending"}</span>;
}

function Stat({ label, value, tone }) {
  return (
    <div style={{ ...card, padding: "14px 18px", minWidth: 108, flex: "1 1 108px" }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: tone }}>{value || 0}</div>
      <div style={{ fontSize: 11.5, color: MUTE, fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function StudentsView({ vendor, referrals, rates, vendorCurrency }) {
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [modalId, setModalId] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const counts = useMemo(() => {
    const c = { total: (referrals || []).length };
    STATUSES.forEach(s => { c[s] = (referrals || []).filter(r => (r.status || "Recommended") === s).length; });
    return c;
  }, [referrals]);

  if (referrals === null)
    return <div style={{ ...card, padding: 30, color: MUTE, fontSize: 14 }}>Loading…</div>;

  const pending = referrals.filter(r => commStatus(r) === "pending");
  const duesINR = pending.reduce((s, r) => s + (toINR(r.commAmount, r.commCurrency, rates) || 0), 0);
  const duesUnknown = pending.filter(r => !r.commAmount).length;

  const shown = referrals
    .filter(r => !filter || (r.status || "Recommended") === filter)
    .filter(r => !q || (r.studentName || "").toLowerCase().includes(q.toLowerCase()));
  const selIds = Object.keys(selected).filter(id => selected[id]);
  const selRefs = referrals.filter(r => selIds.includes(r.id));
  const toggleSel = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const modalRef = referrals.find(r => r.id === modalId);

  return (
    <div>
      <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 800, color: INK }}>Students recommended to you</h1>
      <p style={{ margin: "0 0 18px", fontSize: 14, color: INK2 }}>
        Students NM Squad has introduced to {vendor ? vendor.organization : "you"}. Click a student to update it.
      </p>

      {/* dues pending box */}
      {pending.length > 0 && (
        <div style={{ ...card, padding: "16px 20px", marginBottom: 18, background: "#fdeced", borderColor: "#f0bcbc" }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#b91c1c", letterSpacing: ".06em", textTransform: "uppercase" }}>Commission dues pending to NM Squad</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#b91c1c", marginTop: 4 }}>Rs {money(duesINR)}</div>
          <div style={{ fontSize: 12.5, color: "#9a3b3b", marginTop: 2 }}>
            across {pending.length} student{pending.length > 1 ? "s" : ""}
            {duesUnknown > 0 ? " · " + duesUnknown + " still need an amount entered" : ""}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <Stat label="Total" value={counts.total} tone={INK} />
        <Stat label="In progress" value={counts["In Progress"]} tone={STATUS_FG["In Progress"]} />
        <Stat label="Signed up" value={counts["Signed Up"]} tone={STATUS_FG["Signed Up"]} />
        <Stat label="Did not sign up" value={counts["Did Not Sign Up"]} tone={STATUS_FG["Did Not Sign Up"]} />
        <Stat label="Cancelled" value={counts["Cancelled"]} tone={STATUS_FG["Cancelled"]} />
      </div>

      {referrals.length > 0 && (
        <>
          <input className="vp-in" value={q} onChange={e => setQ(e.target.value)} placeholder="Search students by name"
            style={{ marginBottom: 12, maxWidth: 360 }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            <button className="vp-btn vp-chip" onClick={() => setFilter("")} style={chip(filter === "", INK)}>All ({counts.total})</button>
            {STATUSES.map(s => (
              <button key={s} className="vp-btn vp-chip" onClick={() => setFilter(s)} style={chip(filter === s, STATUS_FG[s])}>
                {s} ({counts[s]})
              </button>
            ))}
          </div>
        </>
      )}

      {selRefs.length > 0 && (
        <div style={{ ...card, padding: "12px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#fffaf0" }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{selRefs.length} student{selRefs.length > 1 ? "s" : ""} selected</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="vp-btn vp-ghost" onClick={() => setSelected({})} style={{ padding: "8px 14px", fontSize: 12.5 }}>Clear</button>
            <button className="vp-btn vp-gold" onClick={() => setBulkOpen(true)} style={{ padding: "8px 16px", fontSize: 12.5 }}>Record bulk payment</button>
          </div>
        </div>
      )}

      {referrals.length === 0 && (
        <div style={{ ...card, padding: "48px 30px", textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>No students yet</div>
          <div style={{ fontSize: 13.5, color: MUTE, marginTop: 6 }}>When NM Squad introduces a student, they will appear here.</div>
        </div>
      )}
      {referrals.length > 0 && shown.length === 0 && (
        <div style={{ ...card, padding: "30px", textAlign: "center", color: MUTE, fontSize: 14 }}>No students match this filter.</div>
      )}

      {shown.length > 0 && (
        <div style={{ ...card, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "#faf8f1" }}>
                <th style={{ ...vth, width: 34 }}></th>
                {["Student", "Programme", "Status", "Commission", ""].map(h => <th key={h} style={vth}>{h}</th>)}
              </tr></thead>
              <tbody>
                {shown.map(r => {
                  const st = r.status || "Recommended";
                  const cs = commStatus(r);
                  return (
                    <tr key={r.id} className="vp-row" style={{ borderTop: `1px solid ${LINE}` }}>
                      <td style={{ ...vtd, textAlign: "center" }}>
                        {cs === "pending"
                          ? <input type="checkbox" checked={!!selected[r.id]} onChange={() => toggleSel(r.id)}
                              style={{ width: 15, height: 15, cursor: "pointer" }} />
                          : null}
                      </td>
                      <td style={{ ...vtd, cursor: "pointer" }} onClick={() => setModalId(r.id)}>
                        <div style={{ fontWeight: 700, color: INK }}>{r.studentName}</div>
                        <div style={{ fontSize: 11.5, color: MUTE }}>{r.studentEmail}</div>
                      </td>
                      <td style={{ ...vtd, color: INK2, cursor: "pointer" }} onClick={() => setModalId(r.id)}>
                        {r.product || "—"}{r.meetingType ? " · " + r.meetingType : ""}
                      </td>
                      <td style={{ ...vtd, cursor: "pointer" }} onClick={() => setModalId(r.id)}>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 999,
                          color: STATUS_FG[st] || INK, background: STATUS_BG[st] || PAPER }}>{st}</span>
                      </td>
                      <td style={{ ...vtd, cursor: "pointer" }} onClick={() => setModalId(r.id)}><CommissionTag r={r} rates={rates} /></td>
                      <td style={{ ...vtd, textAlign: "right", color: "#8a7415", fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer" }}
                        onClick={() => setModalId(r.id)}>More</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalRef && <StudentModal r={modalRef} rates={rates} vendorCurrency={vendorCurrency} onClose={() => setModalId(null)} />}
      {bulkOpen && <BulkPaymentModal refs={selRefs} rates={rates} onClose={() => { setBulkOpen(false); setSelected({}); }} />}
    </div>
  );
}

/* ── bulk commission payment ── */
function CurrencySelect({ value, onChange }) {
  return (
    <select className="vp-in" value={value} onChange={onChange} style={{ cursor: "pointer" }}>
      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

function BulkPaymentModal({ refs, rates, onClose }) {
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const total = refs.reduce((s, r) => s + (toINR(r.commAmount, r.commCurrency, rates) || 0), 0);
  const missing = refs.filter(r => !r.commAmount).length;

  const submit = async () => {
    setBusy(true);
    try {
      for (const r of refs)
        await updateReferral(r.id, { commissionStatus: "paid", commPayment: { date, reference, at: new Date().toISOString() } });
      onClose();
    } catch (e) { alert("Could not save: " + (e.message || "")); setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,26,23,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, padding: 0, maxWidth: 480, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${LINE}` }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: INK }}>Record bulk payment</h3>
          <div style={{ fontSize: 12.5, color: MUTE, marginTop: 3 }}>Marks {refs.length} student{refs.length > 1 ? "s" : ""} as paid</div>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 14 }}>
            {refs.map(r => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: INK2, padding: "6px 0", borderBottom: `1px solid ${PAPER}` }}>
                <span>{r.studentName}</span>
                <span style={{ fontWeight: 700 }}>{r.commAmount ? (r.commCurrency || "") + " " + money(parseNum(r.commAmount)) : "amount not set"}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 800, color: INK, paddingTop: 8 }}>
              <span>Total</span><span>Rs {money(total)}</span>
            </div>
          </div>
          {missing > 0 && <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 10 }}>{missing} student{missing > 1 ? "s have" : " has"} no amount set — open them to add it.</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><div style={labelS}>Date paid</div><input className="vp-in" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
            <div><div style={labelS}>Reference / UTR</div><input className="vp-in" value={reference} onChange={e => setReference(e.target.value)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="vp-btn vp-gold" onClick={submit} disabled={busy} style={{ padding: "11px 20px", fontSize: 14 }}>
              {busy ? "Saving…" : "Mark all as paid"}
            </button>
            <button className="vp-btn vp-ghost" onClick={onClose} style={{ padding: "11px 20px", fontSize: 14 }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── expanded detail for one student ── */
function Row({ label, children }) {
  return (
    <div style={{ borderTop: `1px solid ${LINE}`, padding: "14px 22px" }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: MUTE, letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 9 }}>{label}</div>
      {children}
    </div>
  );
}

function StudentModal({ r, rates, vendorCurrency, onClose }) {
  const status = r.status || "Recommended";
  const cs = commStatus(r);
  const [comm, setComm] = useState({ fee: r.commFee || "", pct: r.commPct || "", amount: r.commAmount || "", currency: r.commCurrency || vendorCurrency || "INR" });
  const [paid, setPaid] = useState({ date: today(), reference: "" });
  const [noteText, setNoteText] = useState("");
  const [flash, setFlash] = useState("");
  const ping = (m) => { setFlash(m); setTimeout(() => setFlash(""), 1800); };
  const notes = r.vendorNotes || (r.vendorNote ? [{ text: r.vendorNote, at: null }] : []);
  const calc = (fee, pct) => { const f = parseNum(fee), p = parseNum(pct); return (f != null && p != null) ? String(Math.round(f * p / 100)) : ""; };
  const inr = toINR(comm.amount, comm.currency, rates);

  const setStatus = (s) => updateReferral(r.id, {
    status: s, statusHistory: [...(r.statusHistory || []), { status: s, at: new Date().toISOString(), by: "vendor" }],
  });
  const saveComm = async () => {
    await updateReferral(r.id, { commFee: comm.fee, commPct: comm.pct, commAmount: comm.amount, commCurrency: comm.currency });
    ping("Commission saved");
  };
  const markPaid = async () => {
    if (!comm.amount) { ping("Enter and save the commission amount first"); return; }
    await updateReferral(r.id, {
      commissionStatus: "paid",
      commFee: comm.fee, commPct: comm.pct, commAmount: comm.amount, commCurrency: comm.currency,
      commPayment: { date: paid.date, reference: paid.reference, at: new Date().toISOString() },
    });
    ping("Marked as paid");
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await updateReferral(r.id, { vendorNotes: [...notes.filter(n => n.at), { text: noteText.trim(), at: new Date().toISOString() }] });
    setNoteText(""); ping("Note added");
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,26,23,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, padding: 0, maxWidth: 560, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${LINE}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: INK }}>{r.studentName}</div>
            <div style={{ fontSize: 12.5, color: MUTE, marginTop: 2 }}>{r.product || "—"}{r.meetingType ? " · " + r.meetingType : ""}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 999, color: STATUS_FG[status] || INK, background: STATUS_BG[status] || PAPER }}>{status}</span>
            <button className="vp-btn vp-ghost" onClick={onClose} style={{ padding: "6px 12px", fontSize: 13 }}>Close</button>
          </div>
        </div>

        {r.intro && <Row label="About the student"><div style={{ fontSize: 13.5, color: INK2, lineHeight: 1.6 }}>{r.intro}</div></Row>}

        <Row label="Update status">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {VENDOR_ACTIONS.map(s => {
              const on = status === s;
              return (
                <button key={s} className="vp-btn" onClick={() => setStatus(s)}
                  style={{ padding: "8px 14px", fontSize: 12.5, borderRadius: 9,
                    border: `1px solid ${on ? STATUS_FG[s] : LINE}`, background: on ? STATUS_FG[s] : "#fff", color: on ? "#fff" : INK2 }}>{s}</button>
              );
            })}
          </div>
        </Row>

        {cs !== "none" && (
          <Row label="Commission">
            {cs === "pending" && (
              <>
                <div className="vp-blink" style={{ fontSize: 13.5, fontWeight: 800, color: "#b91c1c", marginBottom: 6 }}>
                  {comm.amount
                    ? "Dues pending to NM Squad: " + (comm.currency || "INR") + " " + money(parseNum(comm.amount))
                    : "Commission pending — enter the details below"}
                </div>
                {r.commissionPinged && (
                  <div className="vp-blink" style={{ fontSize: 13, fontWeight: 800, color: "#8a5a06", marginBottom: 6 }}>
                    NM Squad has requested this commission.
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div><div style={labelS}>Fee paid by student</div>
                    <input className="vp-in" value={comm.fee} onChange={e => setComm(c => ({ ...c, fee: e.target.value, amount: calc(e.target.value, c.pct) || c.amount }))} /></div>
                  <div><div style={labelS}>Commission %</div>
                    <input className="vp-in" value={comm.pct} onChange={e => setComm(c => ({ ...c, pct: e.target.value, amount: calc(c.fee, e.target.value) || c.amount }))} /></div>
                  <div><div style={labelS}>Commission amount</div>
                    <input className="vp-in" value={comm.amount} onChange={e => setComm(c => ({ ...c, amount: e.target.value }))} /></div>
                  <div><div style={labelS}>Currency</div>
                    <CurrencySelect value={comm.currency} onChange={e => setComm(c => ({ ...c, currency: e.target.value }))} /></div>
                </div>
                {comm.currency !== "INR" && inr != null && (
                  <div style={{ fontSize: 12, color: INK2, marginBottom: 8 }}>Equivalent: <b>Rs {money(inr)}</b> at today's rate</div>
                )}
                <button className="vp-btn vp-ghost" onClick={saveComm} style={{ padding: "8px 16px", fontSize: 13 }}>Save commission</button>

                <div style={{ borderTop: `1px solid ${LINE}`, margin: "16px 0 0", paddingTop: 14 }}>
                  <div style={labelS}>Record your payment to NM Squad</div>
                  <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 8, padding: "10px 13px", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#8a7415", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6 }}>Send your payment to</div>
                    <BankRows />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <input className="vp-in" type="date" value={paid.date} onChange={e => setPaid({ ...paid, date: e.target.value })} />
                    <input className="vp-in" value={paid.reference} onChange={e => setPaid({ ...paid, reference: e.target.value })} placeholder="Reference / UTR" />
                  </div>
                  <button className="vp-btn vp-gold" onClick={markPaid} style={{ padding: "8px 16px", fontSize: 13 }}>Mark as paid</button>
                </div>
              </>
            )}
            {cs === "paid" && (
              <div style={{ fontSize: 13.5, color: "#8a5a06" }}>
                <b>Commission paid{r.commAmount ? " — " + (r.commCurrency || "") + " " + money(parseNum(r.commAmount)) : ""}
                {r.commPayment && r.commPayment.date ? " on " + r.commPayment.date : ""}.</b> Awaiting confirmation from NM Squad.
                {r.commPayment && r.commPayment.reference && <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>Reference: {r.commPayment.reference}</div>}
              </div>
            )}
            {cs === "received" && (
              <div style={{ fontSize: 13.5, fontWeight: 800, color: "#15803d" }}>Commission received by NM Squad. This referral is complete.</div>
            )}
            {flash && <div style={{ fontSize: 12.5, color: "#15803d", fontWeight: 700, marginTop: 8 }}>{flash}</div>}
          </Row>
        )}

        <Row label="Follow-up notes">
          {notes.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {notes.map((n, i) => (
                <div key={i} style={{ fontSize: 13, color: INK2, padding: "6px 0", borderBottom: i < notes.length - 1 ? `1px solid ${PAPER}` : "none" }}>
                  {n.text}{n.at && <span style={{ color: MUTE, fontSize: 11.5 }}>  ·  {fmtDate(n.at)}</span>}
                </div>
              ))}
            </div>
          )}
          <textarea className="vp-in" value={noteText} onChange={e => setNoteText(e.target.value)}
            style={{ minHeight: 54, resize: "vertical", lineHeight: 1.5 }} placeholder="Add a note about this student." />
          <div style={{ marginTop: 8 }}>
            <button className="vp-btn vp-gold" onClick={addNote} style={{ padding: "8px 16px", fontSize: 13 }}>Add note</button>
          </div>
        </Row>
      </div>
    </div>
  );
}

/* ── NM Squad's bank account rows (where vendors send commission) ── */
function BankRows() {
  return NM_BANK.map(([k, v]) => (
    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 14, fontSize: 12.5, padding: "3px 0" }}>
      <span style={{ color: MUTE }}>{k}</span>
      <span style={{ color: INK, fontWeight: 700, textAlign: "right" }}>{v}</span>
    </div>
  ));
}

/* ── My account ── */
function AccountView({ vendor, account, onAccountChange }) {
  const [bank, setBank] = useState((account && account.bankDetails) || { accountName: "", accountNumber: "", ifsc: "", bankName: "", upi: "" });
  const [bankMsg, setBankMsg] = useState("");
  const [currency, setCurrency] = useState((account && account.currency) || "INR");
  const [curMsg, setCurMsg] = useState("");
  const [pw1, setPw1] = useState(""), [pw2, setPw2] = useState(""), [pwMsg, setPwMsg] = useState("");
  const setB = (k) => (e) => setBank({ ...bank, [k]: e.target.value });
  const saveBank = async () => {
    setBankMsg("");
    try {
      await saveVendorAccount(auth.currentUser.uid, { bankDetails: bank });
      onAccountChange({ ...account, bankDetails: bank });
      setBankMsg("Bank details saved.");
    } catch (e) { setBankMsg("Could not save. " + (e.message || "")); }
  };
  const saveCurrency = async () => {
    setCurMsg("");
    try {
      await saveVendorAccount(auth.currentUser.uid, { currency });
      onAccountChange({ ...account, currency });
      setCurMsg("Currency saved.");
    } catch (e) { setCurMsg("Could not save. " + (e.message || "")); }
  };
  const changePw = async () => {
    setPwMsg("");
    if (pw1.length < 8) return setPwMsg("Choose a password of at least 8 characters.");
    if (pw1 !== pw2) return setPwMsg("The two passwords do not match.");
    try { await changeMyPassword(pw1); setPw1(""); setPw2(""); setPwMsg("Password updated."); }
    catch (e) {
      setPwMsg((e.code || "").includes("recent-login")
        ? "For security, sign out and sign back in, then change your password."
        : "Could not update. " + (e.message || ""));
    }
  };
  return (
    <div>
      <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 800, color: INK }}>My account</h1>
      <p style={{ margin: "0 0 22px", fontSize: 14, color: INK2 }}>{vendor ? vendor.organization : ""}</p>

      <div style={{ ...card, padding: 22, marginBottom: 16, borderColor: "#ecd99a", background: "#fffdf3" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 6 }}>NM Squad bank account</div>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 12px" }}>Send all commission payments to this account.</p>
        <BankRows />
      </div>

      <div style={{ ...card, padding: 22, marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 6 }}>Currency</div>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 12px" }}>The default currency for the commissions you record.</p>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ maxWidth: 160 }}><CurrencySelect value={currency} onChange={e => setCurrency(e.target.value)} /></div>
          <button className="vp-btn vp-gold" onClick={saveCurrency} style={{ padding: "10px 18px", fontSize: 13.5 }}>Save currency</button>
          {curMsg && <span style={{ fontSize: 12.5, fontWeight: 700, color: curMsg.includes("Could not") ? "#b91c1c" : "#15803d" }}>{curMsg}</span>}
        </div>
      </div>

      <div style={{ ...card, padding: 22, marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 6 }}>Agreement</div>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 12px" }}>Your Memorandum of Understanding with NM Squad.</p>
        {vendor && vendor.mouUrl
          ? <a href={vendor.mouUrl} target="_blank" rel="noreferrer" className="vp-btn vp-ghost"
              style={{ display: "inline-block", padding: "10px 18px", fontSize: 13.5, textDecoration: "none" }}>Read the MoU document</a>
          : <span style={{ fontSize: 13, color: MUTE }}>Your MoU will be shared by NM Squad.</span>}
      </div>

      <div style={{ ...card, padding: 22, marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 6 }}>Your bank details</div>
        <p style={{ fontSize: 13, color: MUTE, margin: "0 0 14px" }}>Used only for any refund due under the MoU.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {BANK_FIELDS.map(([k, l]) => (
            <div key={k}><div style={labelS}>{l}</div>
              <input className="vp-in" value={bank[k] || ""} onChange={setB(k)} /></div>
          ))}
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <button className="vp-btn vp-gold" onClick={saveBank} style={{ padding: "10px 18px", fontSize: 13.5 }}>Save bank details</button>
          {bankMsg && <span style={{ fontSize: 12.5, fontWeight: 700, color: bankMsg.includes("Could not") ? "#b91c1c" : "#15803d" }}>{bankMsg}</span>}
        </div>
      </div>

      <div style={{ ...card, padding: 22 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 14 }}>Change password</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 420 }}>
          <div><div style={labelS}>New password</div>
            <input className="vp-in" type="password" value={pw1} onChange={e => setPw1(e.target.value)} placeholder="At least 8 characters" /></div>
          <div><div style={labelS}>Confirm</div>
            <input className="vp-in" type="password" value={pw2} onChange={e => setPw2(e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <button className="vp-btn vp-gold" onClick={changePw} style={{ padding: "10px 18px", fontSize: 13.5 }}>Update password</button>
          {pwMsg && <span style={{ fontSize: 12.5, fontWeight: 700, color: pwMsg.includes("updated") ? "#15803d" : "#b91c1c" }}>{pwMsg}</span>}
        </div>
      </div>

      <p style={{ fontSize: 12.5, color: MUTE, marginTop: 22, textAlign: "center" }}>
        Questions? Contact NM Squad at <a href="mailto:nm@neerajmandhana.com" className="vp-link">nm@neerajmandhana.com</a>
      </p>
    </div>
  );
}

function chip(active, fg) {
  return active
    ? { background: fg, color: "#fff", border: `1px solid ${fg}` }
    : { background: "#fff", color: MUTE, border: `1px solid ${LINE}` };
}
